const DESKTOP_MEDIA_QUERY = '(min-width: 1201px)';
const PINNED_ATTRIBUTE_KEY = '_cart_drawer_pinned';

class CartDrawerSidebar {
  constructor(cartDrawerConfig) {
    this.html = document.documentElement;
    this.desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.pinLabel = cartDrawerConfig.dataset.cartDrawerPinLabel || '';
    this.unpinLabel = cartDrawerConfig.dataset.cartDrawerUnpinLabel || '';
    this.isPinned = cartDrawerConfig.dataset.cartDrawerPinned === 'true';
    this.cartReplacementToken = 0;
    this.cartReplacementInProgress = false;
    this.cartReplacementObserver = undefined;
    this.cartReplacementTimeout = undefined;
    this.isOpeningReplacementCart = false;
    this.cartRevealToken = 0;
    this.cartRevealFrame = undefined;
    this.cartRevealAddedNoTransition = false;
    this.pushCloseCleanup = undefined;

    this.syncButtons = this.syncButtons.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleDrawerToggle = this.handleDrawerToggle.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);

    this.setPinned(this.isPinned, false);
    this.html.addEventListener('wetheme-toggle-right-drawer', this.handleDrawerToggle, true);
    this.html.addEventListener('wetheme-cart-update', () => {
      window.requestAnimationFrame(this.syncButtons);
    });
    document.addEventListener('click', this.handleClick, true);

    if (this.desktopMediaQuery.addEventListener) {
      this.desktopMediaQuery.addEventListener('change', this.handleMediaChange);
    } else if (this.desktopMediaQuery.addListener) {
      this.desktopMediaQuery.addListener(this.handleMediaChange);
    }
  }

  isDesktop() {
    return this.desktopMediaQuery.matches;
  }

  isDrawerOpen() {
    return document.body.classList.contains('js-drawer-open-right');
  }

  getCartDrawer() {
    return document.querySelector('.right-drawer-vue .drawer.is--cart');
  }

  hasCartDrawer() {
    return Boolean(this.getCartDrawer());
  }

  isCartDrawerOpen() {
    return Boolean(document.querySelector(
      '.right-drawer-vue .drawer.is--cart'
        + ':not(.drawer-right-leave)'
        + ':not(.drawer-right-leave-active)'
        + ':not(.drawer-right-leave-to)',
    ));
  }

  hasFemtisSidebar() {
    return Boolean(document.querySelector('.f-sidebar-panel'));
  }

  getShopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  persistPinned(value) {
    try {
      window.fetch(this.getShopifyRoot() + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [PINNED_ATTRIBUTE_KEY]: value } }),
        keepalive: true,
      }).catch(() => {});
    } catch (error) {}
  }

  syncButtons() {
    document.querySelectorAll('[data-cart-drawer-pin]').forEach((button) => {
      button.setAttribute('aria-pressed', String(this.isPinned));
      button.setAttribute('aria-label', this.isPinned ? this.unpinLabel : this.pinLabel);
    });
  }

  clearPushCloseWait() {
    if (this.pushCloseCleanup) {
      const cleanup = this.pushCloseCleanup;
      this.pushCloseCleanup = undefined;
      cleanup();
    }
  }

  waitForPushClose() {
    const cartDrawer = this.getCartDrawer();
    const drawerRoot = document.querySelector('.right-drawer-vue');

    if (!cartDrawer || !drawerRoot) {
      this.html.classList.remove('drawer-push-open', 'drawer-push-closing');
      return;
    }

    let disposed = false;
    const onTransitionEnd = (event) => {
      if (event.target === cartDrawer && event.propertyName === 'transform') finish();
    };
    const onTransitionCancel = (event) => {
      if (event.target === cartDrawer && event.propertyName === 'transform') finish();
    };
    const observer = new MutationObserver(() => {
      if (!this.hasCartDrawer()) finish();
    });
    const cleanup = () => {
      if (disposed) return;
      disposed = true;
      cartDrawer.removeEventListener('transitionend', onTransitionEnd);
      cartDrawer.removeEventListener('transitioncancel', onTransitionCancel);
      observer.disconnect();
      if (this.pushCloseCleanup === cleanup) this.pushCloseCleanup = undefined;
    };
    const finish = () => {
      if (this.isDrawerOpen() && this.isCartDrawerOpen()) return;
      cleanup();
      this.html.classList.remove('drawer-push-open', 'drawer-push-closing');
    };

    this.pushCloseCleanup = cleanup;
    cartDrawer.addEventListener('transitionend', onTransitionEnd);
    cartDrawer.addEventListener('transitioncancel', onTransitionCancel);
    observer.observe(drawerRoot, { childList: true, subtree: true });
  }

  setPushOpen(open) {
    this.clearPushCloseWait();

    if (open && this.isDesktop()) {
      this.html.classList.remove('drawer-push-closing');
      this.html.classList.add('drawer-push-open');
      return;
    }

    if (!open && this.isDesktop() && (
      this.html.classList.contains('drawer-push-open')
        || this.html.classList.contains('drawer-push-closing')
    )) {
      this.html.classList.remove('drawer-push-open');
      this.html.classList.add('drawer-push-closing');
      this.waitForPushClose();
      return;
    }

    this.html.classList.remove('drawer-push-open', 'drawer-push-closing');
  }

  cancelCartReveal() {
    this.cartRevealToken += 1;
    if (this.cartRevealFrame) {
      window.cancelAnimationFrame(this.cartRevealFrame);
      this.cartRevealFrame = undefined;
    }

    this.html.classList.remove('drawer-push-cart-reveal');
    if (this.cartRevealAddedNoTransition) {
      this.html.classList.remove('drawer-no-transition');
      this.cartRevealAddedNoTransition = false;
    }
  }

  revealOpenCart() {
    this.cancelCartReveal();

    const cartDrawer = this.getCartDrawer();
    if (!cartDrawer) return;

    const revealToken = this.cartRevealToken;
    this.cartRevealAddedNoTransition = !this.html.classList.contains('drawer-no-transition');
    this.html.classList.add('drawer-no-transition', 'drawer-push-cart-reveal');
    cartDrawer.getBoundingClientRect();

    this.cartRevealFrame = window.requestAnimationFrame(() => {
      this.cartRevealFrame = undefined;
      if (revealToken !== this.cartRevealToken) return;

      if (!cartDrawer.isConnected) {
        this.cancelCartReveal();
        return;
      }

      if (this.cartRevealAddedNoTransition) {
        this.html.classList.remove('drawer-no-transition');
        this.cartRevealAddedNoTransition = false;
        cartDrawer.getBoundingClientRect();
      }
      this.html.classList.remove('drawer-push-cart-reveal');
    });
  }

  clearCartReplacementWait() {
    if (this.cartReplacementObserver) {
      this.cartReplacementObserver.disconnect();
      this.cartReplacementObserver = undefined;
    }

    if (this.cartReplacementTimeout) {
      window.clearTimeout(this.cartReplacementTimeout);
      this.cartReplacementTimeout = undefined;
    }
  }

  openCartAfterReplacement(replacementToken) {
    if (!this.cartReplacementInProgress || replacementToken !== this.cartReplacementToken) return;
    if (this.isDrawerOpen() || this.hasCartDrawer()) return;

    this.clearCartReplacementWait();
    this.cartReplacementInProgress = false;
    this.html.classList.remove('drawer-no-transition');
    this.isOpeningReplacementCart = true;
    try {
      if (window.wetheme && typeof window.wetheme.toggleRightDrawer === 'function') {
        window.wetheme.toggleRightDrawer('cart', true);
      }
    } finally {
      this.isOpeningReplacementCart = false;
    }

    window.setTimeout(() => {
      if (!this.cartReplacementInProgress && replacementToken === this.cartReplacementToken) {
        this.html.classList.remove('drawer-push-handoff');
      }
    }, 800);
  }

  waitForCartRemoval(replacementToken) {
    this.clearCartReplacementWait();

    const check = () => {
      if (!this.cartReplacementInProgress || replacementToken !== this.cartReplacementToken) return;
      if (this.isDrawerOpen() || this.hasCartDrawer()) return;
      this.openCartAfterReplacement(replacementToken);
    };

    const drawerRoot = document.querySelector('.right-drawer-vue');
    if (drawerRoot) {
      this.cartReplacementObserver = new MutationObserver(check);
      this.cartReplacementObserver.observe(drawerRoot, { childList: true, subtree: true });
    }

    check();
    this.cartReplacementTimeout = window.setTimeout(() => {
      if (!this.cartReplacementInProgress || replacementToken !== this.cartReplacementToken) return;
      if (this.isDrawerOpen() || this.hasCartDrawer()) {
        this.clearCartReplacementWait();
        this.cartReplacementInProgress = false;
        this.html.classList.remove('drawer-no-transition', 'drawer-push-handoff');
        this.setPushOpen(false);
        return;
      }
      this.openCartAfterReplacement(replacementToken);
    }, 2000);
  }

  closeFemtisSidebarPanels() {
    this.html.dispatchEvent(new CustomEvent('femtis-close-sidebar-panels'));
  }

  updateCartState(cart) {
    const drawerRoot = document.querySelector('.right-drawer-vue');
    const vueRoot = drawerRoot && drawerRoot.__vue__;
    const cartDrawer = vueRoot && vueRoot.$children
      ? vueRoot.$children.find(component => component.cart !== undefined)
      : null;

    if (cartDrawer) {
      cartDrawer.cart = cart;
      if (cart.attributes && cart.attributes.gift_note) {
        cartDrawer.giftNote = cart.attributes.gift_note;
      }
    }

    if (window.wetheme && typeof window.wetheme.updateCartDrawer === 'function') {
      window.wetheme.updateCartDrawer(cart);
    }
  }

  setPinned(next, shouldPersist) {
    this.isPinned = next;
    this.syncButtons();
    if (shouldPersist) this.persistPinned(this.isPinned ? 'true' : '');
  }

  clearPinned() {
    if (this.isPinned) this.setPinned(false, true);
  }

  handleClick(event) {
    const element = event.target && typeof event.target.closest === 'function' ? event.target : null;
    if (!element) return;

    const pinButton = element.closest('[data-cart-drawer-pin]');
    if (pinButton) {
      event.preventDefault();
      this.setPinned(!this.isPinned, true);
      return;
    }

    if (!this.isDesktop()) return;

    const closeButton = element.closest('.right-drawer-vue .drawer.is--cart .js-drawer-close button');
    if (closeButton) {
      this.clearPinned();
      this.setPushOpen(false);
      return;
    }

    const cartTrigger = element.closest('.js-drawer-open-right-link[aria-controls="CartDrawer"]');
    if (cartTrigger && !this.hasFemtisSidebar() && this.isDrawerOpen() && this.isCartDrawerOpen()) {
      this.clearPinned();
      this.setPushOpen(false);
    }
  }

  handleDrawerToggle(event) {
    window.requestAnimationFrame(this.syncButtons);

    // The replacement open dispatches another toggle event synchronously. Let
    // the theme handle that event without starting a second replacement cycle.
    if (this.isOpeningReplacementCart) return;

    const detail = event.detail || {};
    const type = detail.type;
    const forceOpen = detail.forceOpen;
    const hadCartReplacement = this.cartReplacementInProgress;
    const replacementToken = ++this.cartReplacementToken;

    this.cancelCartReveal();
    this.clearCartReplacementWait();
    if (!hadCartReplacement) this.html.classList.remove('drawer-push-handoff');
    this.cartReplacementInProgress = false;
    if (hadCartReplacement) this.html.classList.remove('drawer-no-transition', 'drawer-push-handoff');

    if (type === 'cart') {
      const drawerIsOpen = this.isDrawerOpen();
      const cartIsOpen = this.isCartDrawerOpen();
      const femtisSidebarIsOpen = this.hasFemtisSidebar();
      const shouldOpen = forceOpen === true || (forceOpen !== false && (!drawerIsOpen || femtisSidebarIsOpen));

      if (shouldOpen) {
        if (drawerIsOpen && femtisSidebarIsOpen && window.wetheme && typeof window.wetheme.toggleRightDrawer === 'function') {
          if (this.isDesktop() && cartIsOpen) {
            // The cart is already open. Only close the product sidebar and stop
            // the theme from calling open() a second time.
            if (detail.params && detail.params.cart) this.updateCartState(detail.params.cart);
            event.stopImmediatePropagation();
            this.revealOpenCart();
            this.closeFemtisSidebarPanels();
            this.setPushOpen(true);
            return;
          }

          this.cartReplacementInProgress = true;
          detail.forceOpen = false;
          if (this.isDesktop()) {
            this.html.classList.add('drawer-no-transition', 'drawer-push-handoff');
          }
          this.waitForCartRemoval(replacementToken);
          this.closeFemtisSidebarPanels();
          this.setPushOpen(true);
          return;
        }
        this.closeFemtisSidebarPanels();
        this.setPushOpen(true);
      } else {
        if (cartIsOpen && this.isPinned) this.clearPinned();
        this.setPushOpen(false);
      }
      return;
    }

    if (forceOpen !== false) this.setPushOpen(false);
  }

  handleMediaChange() {
    this.clearPushCloseWait();

    if (this.isDesktop()) {
      if (this.isDrawerOpen() && this.isCartDrawerOpen()) this.setPushOpen(true);
    } else {
      this.html.classList.remove(
        'drawer-push-open',
        'drawer-push-closing',
        'drawer-no-transition',
        'drawer-push-handoff',
      );
    }
  }
}

const cartDrawerConfig = document.querySelector('.right-drawer-vue[data-cart-drawer-pinned]');
if (cartDrawerConfig) {
  new CartDrawerSidebar(cartDrawerConfig);
  window.wetheme?.webcomponentRegistry?.register({ key: 'cart-drawer-sidebar' });
}
